import { useContext, useEffect, useMemo, useState } from "react";
import Autocomplete from "@mui/material/Autocomplete";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import IconButton from "@mui/material/IconButton";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import AddCircleOutlineRoundedIcon from "@mui/icons-material/AddCircleOutlineRounded";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import { AlertComponent, ModalComponent } from "../../../components";
import { DocumentsTopNavbar } from "../../../components/documents/DocumentsTopNavbar";
import { DeleteEntryDialog } from "../../../components/documents/pump/DeleteEntryDialog";
import {
  ReportForm,
  ReportFormValues,
  SUGGESTED_REPORT_TYPES,
} from "../../../components/common/ReportForm";
import {
  ListPageLayout,
  ListColumn,
  ListFilterField,
} from "../../../components/shared/ListPageLayout";
import DataContext from "../../../context/DataContext";
import { useFetch } from "../../../hooks/useFetch";
import { usePaginator } from "../../../hooks/usePaginator";
import { usePermissions } from "../../../hooks/usePermissions";
import { useRequest } from "../../../hooks/useRequest";
import { ClientProps, ReportProps } from "../../../interfaces/interfaces";
import { buildReportFormData } from "./reportFormData";

interface ReportsResponse {
  succes?: boolean;
  success?: boolean;
  data: ReportProps[];
}

interface Props {
  clientMode?: boolean;
  assignedClientOptions?: ClientProps[];
  assignedClientId?: number | null;
  onAssignedClientChange?: (clientId: number | null) => void;
  embedded?: boolean;
}

const formatReportDate = (value?: string | null) => {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";
  return date.toLocaleDateString("en-US", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  });
};

export const ReportsAdmin = ({
  clientMode = false,
  assignedClientOptions = [],
  assignedClientId = null,
  onAssignedClientChange,
  embedded = false,
}: Props) => {
  const { paginationModel, setPaginationModel } = usePaginator("reports");
  const { clientOptions: globalClientOptions, fetchDataClient } =
    useContext(DataContext);
  const { handleRequest } = useRequest();
  const { canManageReports } = usePermissions();
  const clientOptions = clientMode ? assignedClientOptions : globalClientOptions;

  const [clientId, setClientId] = useState("");
  const [reportType, setReportType] = useState("");
  const [search, setSearch] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");

  const [formOpen, setFormOpen] = useState(false);
  const [editingRow, setEditingRow] = useState<ReportProps | null>(null);
  const [deletingRow, setDeletingRow] = useState<ReportProps | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [isBusy, setIsBusy] = useState(false);
  const [isSubmittingReport, setIsSubmittingReport] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [isSuccess, setIsSuccess] = useState(false);
  const [isError, setIsError] = useState(false);

  useEffect(() => {
    if (!clientMode) fetchDataClient();
    // Master data is intentionally loaded once per page mount.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientMode]);

  useEffect(() => {
    if (!clientMode || !assignedClientId) return;
    setClientId(String(assignedClientId));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [assignedClientId, clientMode]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setIsSuccess(false);
      setIsError(false);
    }, 4000);
    return () => clearTimeout(timer);
  }, [isSuccess, isError]);

  const fetchFilters = useMemo(
    () => ({ clientId, reportType, search, dateFrom, dateTo }),
    [clientId, reportType, search, dateFrom, dateTo],
  );
  const { data, isLoading, error, fetchData, pagination } =
    useFetch<ReportsResponse>(
      "report-paginate",
      paginationModel,
      undefined,
      undefined,
      fetchFilters,
    );
  const rows = data?.data || [];

  const chooseClient = (value: string) => {
    const nextClientId =
      clientMode && !value && assignedClientId
        ? String(assignedClientId)
        : value;
    setClientId(nextClientId);
    if (clientMode) {
      onAssignedClientChange?.(nextClientId ? Number(nextClientId) : null);
    }
    // Client lives in toolbarActions, not the `filters` array, so
    // ListPageLayout's own filterSignature effect won't reset the page for
    // it — reset explicitly here, same as ProposalAdmin's sidePanel.onSelect.
    setPaginationModel({ ...paginationModel, page: 0 });
  };

  const submitReport = async (values: ReportFormValues) => {
    setIsSubmittingReport(true);
    setFormError(null);
    try {
      const formData = buildReportFormData(values);

      const endpoint = editingRow
        ? `${process.env.REACT_APP_DEV_API}/report/${editingRow.id}`
        : `${process.env.REACT_APP_DEV_API}/report-upload`;
      const response = await fetch(endpoint, {
        method: editingRow ? "PUT" : "POST",
        body: formData,
        headers: { Authorization: `Bearer ${localStorage.getItem("info")}` },
      });
      const result = await response.json().catch(() => null);
      if (!response.ok || !result?.success) {
        throw new Error(result?.error || "The report could not be saved");
      }
      setIsSuccess(true);
      fetchData();
      setFormOpen(false);
      setEditingRow(null);
    } catch (submitError) {
      setFormError(
        submitError instanceof Error
          ? submitError.message
          : "The report could not be saved",
      );
    } finally {
      setIsSubmittingReport(false);
    }
  };

  const confirmDeleteRow = async () => {
    if (!deletingRow) return;
    setDeleting(true);
    try {
      const response = await handleRequest({
        endpoint: `report/${deletingRow.id}`,
        options: { method: "DELETE" },
      });
      if (!response.success) throw new Error("Delete failed");
      setDeletingRow(null);
      fetchData();
    } catch {
      setIsError(true);
    } finally {
      setDeleting(false);
    }
  };

  const previewReport = async (row: ReportProps) => {
    const win = window.open("", "_blank");
    setIsBusy(true);
    try {
      const response = await handleRequest({
        endpoint: `report-download/${row.id}`,
        options: { method: "GET" },
      });
      if (!response.success) throw new Error("Download failed");
      const mimeType =
        response.meta?.mimeType || row.mimeType || "text/html";
      const url = window.URL.createObjectURL(
        new Blob([new Uint8Array(response.data.data).buffer], {
          type: mimeType,
        }),
      );
      if (!win) throw new Error("Preview window was blocked");
      win.opener = null;
      win.document.title = row.title;
      win.document.body.style.margin = "0";
      const iframe = win.document.createElement("iframe");
      iframe.title = row.title;
      iframe.setAttribute("sandbox", "allow-scripts");
      iframe.style.width = "100vw";
      iframe.style.height = "100vh";
      iframe.style.border = "0";
      iframe.src = url;
      iframe.addEventListener("load", () => {
        window.setTimeout(() => window.URL.revokeObjectURL(url), 1000);
      });
      win.document.body.replaceChildren(iframe);
    } catch {
      win?.close();
      setIsError(true);
    } finally {
      setIsBusy(false);
    }
  };

  const columns: ListColumn<ReportProps>[] = [
    {
      key: "title",
      header: "Title",
      render: (r) => (
        <Typography variant="body2" fontWeight={850}>
          {r.title}
        </Typography>
      ),
      width: "1.8fr",
    },
    { key: "client", header: "Client", render: (r) => r.client?.name || "—", width: "1.2fr" },
    { key: "reportType", header: "Report Type", render: (r) => r.reportType, width: "1fr" },
    { key: "period", header: "Period", render: (r) => r.period, width: "1fr" },
    {
      key: "source",
      header: "Source",
      render: (r) => (
        <Chip
          label={r.sourceType === "html" ? "HTML uploaded" : "Excel generated"}
          size="small"
          sx={{
            bgcolor: r.sourceType === "html" ? "#EDF1F4" : "#FFF2DC",
            color: r.sourceType === "html" ? "#4D5967" : "#875100",
            fontSize: 11.5,
            fontWeight: 750,
          }}
        />
      ),
      width: "150px",
    },
    { key: "date", header: "Uploaded", render: (r) => formatReportDate(r.date), width: "120px" },
  ];

  // Client is intentionally NOT part of `filters`: ListPageLayout's built-in
  // "Clear filters" button calls `onChange("")` on every entry in `filters`,
  // which would wipe the selected client too. The rest of the migrated family
  // (ProposalAdmin, Chemical Tracker, Tally Doc, Pulling Report, Additional
  // Documents) keeps its client/well selector out of `filters` for the same
  // reason — it's rendered via `toolbarActions` instead, below.
  const filters: ListFilterField[] = [
    {
      kind: "autocomplete",
      key: "reportType",
      label: "Report type",
      value: reportType,
      onChange: setReportType,
      options: SUGGESTED_REPORT_TYPES.map((t) => ({ value: t, label: t })),
      placeholder: "e.g. Performance",
    },
    { kind: "date", key: "dateFrom", label: "From date", value: dateFrom, onChange: setDateFrom },
    { kind: "date", key: "dateTo", label: "To date", value: dateTo, onChange: setDateTo },
  ];

  const list = (
    <ListPageLayout<ReportProps>
      title="Reports"
      columns={columns}
      rows={rows}
      getRowId={(r) => r.id}
      rowsLoading={isLoading || isBusy}
      isError={Boolean(error)}
      errorMessage="Reports could not be loaded."
      emptyTitle="No reports found"
      emptySubtitle={
        clientMode
          ? "Adjust the filters to find dashboard reports."
          : "Select a client, adjust filters, generate from Excel, or upload ready HTML."
      }
      searchValue={search}
      onSearchChange={(value) => setSearch(value.trim())}
      searchPlaceholder="Title…"
      filters={filters}
      page={paginationModel.page}
      pageSize={paginationModel.pageSize}
      totalCount={Number(pagination.totalRecords || 0)}
      onPageChange={(page) => setPaginationModel({ ...paginationModel, page })}
      onPageSizeChange={(pageSize) => setPaginationModel({ page: 0, pageSize })}
      toolbarActions={
        <Box sx={{ display: "flex", alignItems: "flex-end", gap: "10px" }}>
          <Box sx={{ width: 220 }}>
            <Box
              component="label"
              sx={{
                display: "block",
                mb: "5px",
                color: "#9CA1AB",
                fontSize: 10.5,
                fontWeight: 600,
                letterSpacing: ".6px",
                lineHeight: 1.2,
                textTransform: "uppercase",
              }}
            >
              Client
            </Box>
            <Autocomplete
              size="small"
              options={clientOptions}
              value={
                clientOptions.find((client) => String(client.id) === clientId) ||
                null
              }
              getOptionLabel={(client) => client.name}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              onChange={(_, client) => chooseClient(client ? String(client.id) : "")}
              disableClearable={clientMode && clientOptions.length === 1}
              disabled={clientMode && clientOptions.length === 1}
              autoHighlight
              openOnFocus
              sx={{
                "& .MuiOutlinedInput-root": {
                  minHeight: 38,
                  bgcolor: "#FAFAF8",
                  borderRadius: "9px",
                  fontSize: 13,
                  "& fieldset": { borderColor: "#E2E2DD" },
                  "&:hover fieldset": { borderColor: "#C9C7BE" },
                  "&.Mui-focused fieldset": { borderColor: "#ED8B00", borderWidth: "1px" },
                },
                "& .MuiInputBase-input": { py: "8px" },
              }}
              renderInput={(params) => (
                <TextField {...params} placeholder="Search clients…" />
              )}
            />
          </Box>
          {canManageReports ? (
            <Button
              variant="outlined"
              startIcon={<AddCircleOutlineRoundedIcon />}
              onClick={() => {
                setEditingRow(null);
                setFormError(null);
                setFormOpen(true);
              }}
              sx={{
                borderColor: "#E7E7E3",
                color: "#2A2F38",
                borderRadius: "9px",
                px: "15px",
                py: "8px",
                fontSize: 13,
                fontWeight: 600,
                textTransform: "none",
                "&:hover": { borderColor: "#ED8B00", color: "#ED8B00" },
              }}
            >
              Add Report
            </Button>
          ) : null}
        </Box>
      }
      rowActions={(r) => (
        <>
          <IconButton size="small" onClick={() => previewReport(r)} title="Preview">
            <VisibilityOutlinedIcon fontSize="small" />
          </IconButton>
          {canManageReports ? (
            <IconButton
              size="small"
              onClick={() => {
                setEditingRow(r);
                setFormError(null);
                setFormOpen(true);
              }}
              title="Edit"
            >
              <EditOutlinedIcon fontSize="small" />
            </IconButton>
          ) : null}
          {canManageReports ? (
            <IconButton size="small" onClick={() => setDeletingRow(r)} title="Delete">
              <DeleteOutlineIcon fontSize="small" />
            </IconButton>
          ) : null}
        </>
      )}
    />
  );

  const modals = (
    <>
      {formOpen ? (
        <ModalComponent
          modalContent={
            <ReportForm
              clientOptions={clientOptions}
              initialValues={
                editingRow
                  ? {
                      client: editingRow.client ?? undefined,
                      reportType: editingRow.reportType,
                      sourceType: editingRow.sourceType || "excel",
                      title: editingRow.title,
                      period: editingRow.period,
                    }
                  : undefined
              }
              submitting={isSubmittingReport}
              error={formError}
              onSubmit={submitReport}
              onCancel={() => {
                setFormOpen(false);
                setEditingRow(null);
                setFormError(null);
              }}
            />
          }
        />
      ) : null}
      <DeleteEntryDialog
        open={Boolean(deletingRow)}
        onClose={() => setDeletingRow(null)}
        onConfirm={confirmDeleteRow}
        confirming={deleting}
        title="Delete report"
        description="This will permanently remove this report. This action cannot be undone."
      />
      {isSuccess ? <AlertComponent type="success" /> : null}
      {isError ? <AlertComponent type="error" /> : null}
    </>
  );

  if (embedded) {
    return (
      <>
        {list}
        {modals}
      </>
    );
  }

  return (
    <Box sx={{ minHeight: "100vh", bgcolor: "#F3F3F0", color: "#29313D" }}>
      <DocumentsTopNavbar title="Reports" />
      <Box sx={{ maxWidth: 1680, mx: "auto", p: { xs: 2, md: 3.5 } }}>{list}</Box>
      {modals}
    </Box>
  );
};
